import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { ApiUrl } from "../../../ApiUrl";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import AdmAddInventory from "./AdmAddInventory";

const AdmInventorySearch = () => {
  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId");
  const branchId = localStorage.getItem("branchId");
  const userId = sessionStorage.getItem("userId");

  // View Toggle State: 'search', 'add', 'edit'
  const [viewMode, setViewMode] = useState("search");
  const [editingItem, setEditingItem] = useState(null);

  // Search Filter State
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [itemNameFilter, setItemNameFilter] = useState("");
  const [inventoryType, setInventoryType] = useState(null);
  const [inventoryLocation, setInventoryLocation] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  // Dropdown Lists State
  const [categories, setCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dropdown Options
  const typeOptions = [
    { value: "Consumable", label: "Consumable" },
    { value: "Non-Consumable", label: "Non-Consumable" },
    { value: "Asset", label: "Asset" },
  ];

  const locationOptions = [
    { value: "Medical", label: "Medical" },
    { value: "Hostel", label: "Hostel" },
    { value: "Nursing College", label: "Nursing College" },
  ];

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
    { value: "Lost", label: "Lost" },
    { value: "Damaged", label: "Damaged" },
    { value: "Others", label: "Others" },
  ];

  // Fetch Category List
  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${ApiUrl.apiurl}INVENTORY/categories/?org_id=${orgId}&branch_id=${branchId}`
      );
      const data = await response.json();
      if (response.ok) {
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch Sub-Category List
  const fetchSubCategories = async () => {
    try {
      const response = await fetch(
        `${ApiUrl.apiurl}INVENTORY/subcategories/?org_id=${orgId}&branch_id=${branchId}`
      );
      const data = await response.json();
      if (response.ok) {
        setAllSubCategories(data);
      }
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    handleSearch(); // Fetch initial list on load
  }, []);

  // Handle Search
  const handleSearch = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        org_id: orgId || "",
        branch_id: branchId || "",
        from_date: fromDate || "",
        to_date: toDate || "",
      });

      if (selectedCategory) queryParams.append("category_id", selectedCategory.value);
      if (selectedSubCategory) queryParams.append("subcategory_id", selectedSubCategory.value);
      if (itemNameFilter.trim()) queryParams.append("name", itemNameFilter.trim());
      if (inventoryType) queryParams.append("inventory_type", inventoryType.value);
      if (inventoryLocation) queryParams.append("inventory_location", inventoryLocation.value);
      if (statusFilter) queryParams.append("status", statusFilter.value);

      const response = await fetch(
        `${ApiUrl.apiurl}INVENTORY/items/?${queryParams.toString()}`
      );
      const data = await response.json();
      if (response.ok) {
        setItemsList(data);
      } else {
        console.error("Error fetching items:", data);
        alert("Failed to fetch inventory items.");
      }
    } catch (error) {
      console.error("Search error:", error);
      alert("An error occurred during search.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Clear
  const handleClear = () => {
    setFromDate(new Date().toISOString().split("T")[0]);
    setToDate(new Date().toISOString().split("T")[0]);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setItemNameFilter("");
    setInventoryType(null);
    setInventoryLocation(null);
    setStatusFilter(null);
    setItemsList([]);
  };

  // Export List to PDF
  const handleExportPDF = () => {
    if (itemsList.length === 0) {
      alert("No data available to export.");
      return;
    }

    const doc = new jsPDF("landscape", "mm", "a4");

    // Title
    doc.setFontSize(14);
    doc.setFont("Helvetica", "bold");
    doc.text("INVENTORY REPORT", 14, 15);

    // Filter information
    doc.setFontSize(9);
    doc.setFont("Helvetica", "normal");
    doc.text(`Period: ${fromDate} to ${toDate}`, 14, 21);

    const headers = [
      [
        "Sr No",
        "Category",
        "Sub Category",
        "Location",
        "Item Name",
        "Status",
        "Qty",
        "Value",
        "Type",
        "Purchase Date",
      ],
    ];

    const body = itemsList.map((item, idx) => [
      idx + 1,
      item.category_name,
      item.subcategory_name,
      item.inventory_location,
      item.item_name,
      item.status,
      item.quantity,
      item.value,
      item.inventory_type,
      item.purchase_date,
    ]);

    doc.autoTable({
      startY: 25,
      head: headers,
      body: body,
      theme: "grid",
      headStyles: { fillColor: [25, 118, 210] },
      styles: { fontSize: 8, cellPadding: 2 },
    });

    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    window.open(blobUrl, "_blank");
  };

  // View Single Item Details PDF
  const handleViewItemPDF = (item) => {
    const doc = new jsPDF("portrait", "mm", "a4");

    doc.setFontSize(14);
    doc.setFont("Helvetica", "bold");
    doc.text("INVENTORY ITEM SLIP", 14, 15);
    doc.line(14, 18, 196, 18);

    const details = [
      ["Item Name", item.item_name],
      ["Category", item.category_name],
      ["Sub-Category", item.subcategory_name],
      ["Inventory Type", item.inventory_type],
      ["Inventory Location", item.inventory_location],
      ["Purchase Date", item.purchase_date],
      ["Quantity", item.quantity],
      ["Value", `$${item.value}`],
      ["Status", item.status],
      ["Remarks", item.remarks || "-"],
    ];

    doc.autoTable({
      startY: 23,
      body: details,
      theme: "plain",
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: "bold", width: 50 },
      },
    });

    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    window.open(blobUrl, "_blank");
  };

  const categoryOptions = categories.map((cat) => ({
    value: cat.category_id,
    label: cat.category_name,
  }));

  const filteredSubCategories = selectedCategory
    ? allSubCategories.filter((sub) => sub.category === selectedCategory.value)
    : [];

  const subCategoryOptions = filteredSubCategories.map((sub) => ({
    value: sub.subcategory_id,
    label: sub.subcategory_name,
  }));

  const filterPanelStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #dee2e6",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  };

  if (viewMode === "add") {
    return (
      <AdmAddInventory
        onClose={() => setViewMode("search")}
        onSaveSuccess={() => {
          setViewMode("search");
          handleSearch();
        }}
      />
    );
  }

  if (viewMode === "edit") {
    return (
      <AdmAddInventory
        editingItem={editingItem}
        onClose={() => {
          setViewMode("search");
          setEditingItem(null);
        }}
        onSaveSuccess={() => {
          setViewMode("search");
          setEditingItem(null);
          handleSearch();
        }}
      />
    );
  }

  return (
    <div className="container-fluid ">
      <div className="row ">
        <div className="col-12">
          <div className="card p-0 ">
            <div className="card-body ">
              {/* Page Title */}
              <p
                style={{
                  marginBottom: "0px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                SEARCH INVENTORY
              </p>

              {/* Action Buttons */}
              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: "150px" }}
                    onClick={() => setViewMode("add")}
                  >
                    New Inventory
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: "150px" }}
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: "150px" }}
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ width: "150px" }}
                    onClick={() => navigate("/admin/dashboard")}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: "150px" }}
                    onClick={handleExportPDF}
                  >
                    Export to PDF
                  </button>
                </div>
              </div>

              {/* Filters */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearch();
                }}
              >
                <div className="row mt-3 mx-2">
                  <div className="col-12 custom-section-box">
                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                      <div className="row flex-grow-1 mt-3">
                        {/* Row 1 */}
                        <div className="col-12 col-md-3 mb-2">
                          <label className="form-label fw-bold">
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control detail"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3 mb-3">
                          <label className="form-label fw-bold">To Date</label>
                          <input
                            type="date"
                            className="form-control detail"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3 mb-3">
                          <label
                            htmlFor="inventory-category"
                            className="form-label "
                          >
                            Inventory Category
                          </label>
                          <Select
                            options={categoryOptions}
                            value={selectedCategory}
                            onChange={(option) => {
                              setSelectedCategory(option);
                              setSelectedSubCategory(null);
                            }}
                            className="detail"
                            placeholder="All Categories"
                          />
                        </div>

                        <div className="col-12 col-md-3 mb-3">
                          <label
                            htmlFor="inventory-sub-category"
                            className="form-label "
                          >
                            Inventory Sub-Category
                          </label>
                          <Select
                            options={subCategoryOptions}
                            value={selectedSubCategory}
                            onChange={(option) =>
                              setSelectedSubCategory(option)
                            }
                            placeholder="All Sub-Categories"
                            isDisabled={!selectedCategory}
                            className="detail"
                          />
                        </div>

                        {/* Row 2 */}
                        <div className="col-12 col-md-3 mb-3">
                          <label htmlFor="name" className="form-label fw-bold">
                            Name
                          </label>
                          <input
                            type="text"
                            className="form-control detail"
                            placeholder="Enter Name"
                            value={itemNameFilter}
                            onChange={(e) => setItemNameFilter(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3 mb-3">
                          <label
                            htmlFor="inventory-type"
                            className="form-label "
                          >
                            Inventory Type
                          </label>
                          <Select
                            options={typeOptions}
                            value={inventoryType}
                            onChange={(option) => setInventoryType(option)}
                            placeholder="All Types"
                            className="detail"
                          />
                        </div>

                        <div className="col-12 col-md-3 mb-3">
                          <label
                            htmlFor="inventory-location"
                            className="form-label"
                          >
                            Inventory Location
                          </label>
                          <Select
                            options={locationOptions}
                            value={inventoryLocation}
                            onChange={(option) => setInventoryLocation(option)}
                            placeholder="All Locations"
                            className="detail"
                          />
                        </div>

                        <div className="col-12 col-md-3 mb-3">
                          <label htmlFor="status" className="form-label">
                            Status
                          </label>
                          <Select
                            options={statusOptions}
                            value={statusFilter}
                            onChange={(option) => setStatusFilter(option)}
                            placeholder="All Status"
                            className="detail"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>

              {/* Results Table */}
              <div className="col-12">
                <div className="table-responsive">
                  <table
                    className="table table-bordered "
                  >
                    <thead >
                      <tr>
                        <th>Sr No</th>
                        <th>Category</th>
                        <th>Sub Category</th>
                        <th>Inventory Location</th>
                        <th>Name</th>
                        <th>Created By</th>
                        <th>Status</th>
                        <th>Remarks</th>
                        <th>Quantity</th>
                        <th>Value</th>
                        <th>Type</th>
                        <th>Purchase Date</th>
                        <th>View</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsList.length === 0 ? (
                        <tr>
                          <td
                            colSpan="14"
                            className="text-center text-muted py-4"
                          >
                            No records found
                          </td>
                        </tr>
                      ) : (
                        itemsList.map((item, index) => (
                          <tr key={item.item_id}>
                            <td className="text-center">{index + 1}</td>
                            <td>{item.category_name}</td>
                            <td>{item.subcategory_name}</td>
                            <td>{item.inventory_location}</td>
                            <td>{item.item_name}</td>
                            <td>{item.created_by_name || item.created_by || "-"}</td>
                            <td>{item.status}</td>
                            <td
                              style={{
                                maxWidth: "150px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.remarks || "-"}
                            </td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-center">{item.value}</td>
                            <td>{item.inventory_type}</td>
                            <td>{item.purchase_date}</td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-link text-decoration-none p-0 fw-semibold"
                                onClick={() => handleViewItemPDF(item)}
                              >
                                View
                              </button>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-link text-decoration-none p-0 fw-semibold"
                                onClick={() => {
                                  setEditingItem(item);
                                  setViewMode("edit");
                                }}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmInventorySearch;
